import { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import { CrudPage } from "../components/CrudPage";
import { api } from "../lib/api";
import { useLang } from "../lib/i18n";
import { useAuth } from "../contexts/AuthContext";
import { fmtIDR, fmtNum } from "../lib/format";

export default function Ingredients() {
  const { t } = useLang();
  const { user } = useAuth();
  const { outlet } = useOutletContext() || {};
  const restricted = user && user.role !== "owner" && !!user.outlet_id;
  const [meta, setMeta] = useState({ categories: [], units: [] });

  useEffect(() => { api.get("/meta/all").then((r) => setMeta(r.data)).catch(() => {}); }, []);
  const catOptions = meta.categories.filter((c) => c.type === "ingredient").map((c) => ({ value: c.id, label: c.name }));
  const unitOptions = meta.units.map((u) => ({ value: u.id, label: `${u.name} (${u.abbreviation})` }));
  const catName = (id) => meta.categories.find((c) => c.id === id)?.name || "-";
  const unitAbbr = (id) => meta.units.find((u) => u.id === id)?.abbreviation || "";
  const outletOptions = (meta.outlets || []).map((o) => ({ value: o.id, label: o.name }));
  const outletName = (id) => (meta.outlets || []).find((o) => o.id === id)?.name || "-";
  const warehouseId = (meta.outlets || []).find((o) => o.is_warehouse)?.id || "";

  return (
    <CrudPage
      title={t("nav_ingredients")}
      endpoint="ingredients"
      testPrefix="ingredients"
      defaults={{ outlet_id: warehouseId }}
      transformItems={(items) => items.flatMap((i) => {
        const row = (oid) => ({ ...i, _rowKey: `${i.id}-${oid || "x"}`, _rowOutlet: oid, _rowQty: oid ? ((i.by_outlet || {})[oid] ?? 0) : i.stock });
        if (restricted) return [row(user.outlet_id)];
        if (outlet) return [row(outlet)];
        const entries = Object.entries(i.by_outlet || {}).filter(([, v]) => v !== 0);
        if (!entries.length) return [row("")];
        return entries.map(([oid]) => row(oid));
      })}
      columns={[
        { key: "name", label: t("name") },
        { key: "category_id", label: t("category"), render: (i) => catName(i.category_id) },
        { key: "stock", label: t("stock"), render: (i) => {
            const shown = i._rowQty ?? (restricted ? (i.outlet_stock ?? 0) : i.stock);
            return <span className={`font-mono font-semibold ${i.min_stock > 0 && shown <= i.min_stock ? "text-rose-600" : ""}`}>{fmtNum(shown)} {unitAbbr(i.unit_id)}</span>;
          } },
        { key: "_rowOutlet", label: t("outlet"), render: (i) => <span className="text-xs text-muted-foreground">{i._rowOutlet ? outletName(i._rowOutlet) : "-"}</span> },
        { key: "purchase_price", label: t("purchasePrice"), render: (i) => i.purchase_price ? <span className="font-mono">{fmtIDR(i.purchase_price)} <span className="text-muted-foreground text-xs">/ {fmtNum(i.purchase_qty)} {unitAbbr(i.unit_id)}</span></span> : "-" },
        { key: "price", label: t("pricePerUnit"), render: (i) => <span className="font-mono font-bold text-amber-600">{fmtIDR(i.price)}<span className="text-muted-foreground font-normal text-xs">/{unitAbbr(i.unit_id)}</span></span> },
        { key: "avg_cost", label: "Avg Cost", render: (i) => <span className="font-mono text-muted-foreground">{fmtIDR(i.avg_cost)}</span> },
        { key: "min_stock", label: t("minStock"), render: (i) => <span className="font-mono">{fmtNum(i.min_stock)}</span> },
      ]}
      fields={[
        { name: "name", label: t("name") },
        { name: "category_id", label: t("category"), type: "select", options: catOptions },
        { name: "unit_id", label: t("unit"), type: "select", options: unitOptions },
        { name: "outlet_id", label: `${t("outlet")} (stok awal masuk ke outlet ini)`, type: "select", options: outletOptions },
        { name: "purchase_qty", label: `${t("purchaseQty")} (misal: 1000 g)`, type: "number" },
        { name: "purchase_price", label: `${t("purchasePrice")} (per kemasan)`, type: "number" },
        { name: "_auto_price", label: t("pricePerUnit"), type: "computed", render: (f) => `${fmtIDR((Number(f.purchase_price) || 0) / (Number(f.purchase_qty) || 1))} / ${unitAbbr(f.unit_id) || "satuan"}` },
        { name: "stock", label: `${t("stock")} (ubah = adjustment otomatis)`, type: "number" },
        { name: "min_stock", label: t("minStock"), type: "number" },
      ]}
    />
  );
}
